/**
 * PSTUDIO-3968 — decide WHERE a paste's payload comes from, and refuse to invent
 * one.
 *
 * The bug this exists to make impossible: `handlePaste` used to `await
 * navigator.clipboard.readText()` and, on rejection, fall back to
 * `clipboardDetails.valueAtTimeOfCopy || ''`. For an EXTERNAL copy that in-memory
 * value is empty, so a real Chrome that never granted `clipboard-read` wrote `''`
 * into the focused cell — the paste did not merely fail, it CLEARED the cell.
 * Proven live on a `main` build: 5 of 18 matrix cases, all on the "external
 * clipboard, clipboard-read revoked" axis, turned a seeded value into `""`.
 *
 * Resolution order (product decision — mirrored in
 * `src/stores/KEYBOARD_CLIPBOARD_BEHAVIOR.md` guard rail 17):
 *   1. `event` — the native `ClipboardEvent`'s own data. The ONLY source that
 *      works without `clipboard-read`, which is why it wins.
 *   2. `system` — the Async Clipboard API read, but only when it actually
 *      RESOLVED with content. A rejection is never coerced into content.
 *   3. `memory` — the in-app copy buffer, but ONLY when it is trustworthy (see
 *      `canTrustMemory` below).
 *   4. `unavailable` — the caller writes NOTHING.
 *
 * ## Why the in-memory buffer needs a trust rule
 *
 * `clipboardDetails.valueAtTimeOfCopy` is never cleared — not by
 * `clearCellClipboard()`, not by Escape — so it can hold a value from an in-app
 * Ctrl+C made minutes ago. Falling back to it whenever the system clipboard reads
 * back EMPTY reintroduces the very bug class this helper exists for, just with a
 * different symptom: copy a cell in the table, then copy an IMAGE or a FILE in
 * another app (no `text/plain` at all), then paste → the stale in-app value lands
 * in the cell. That is exactly the "it still pastes the old one" report from the
 * release thread.
 *
 * So an empty-but-readable clipboard is NOT a licence to use the buffer. The
 * buffer is trustworthy only when the system clipboard could not speak for
 * itself:
 *   - the async read was REJECTED (`{ok: false}`) — we know nothing about the OS
 *     clipboard, and the in-app copy is the best available truth;
 *   - no read was attempted at all (`systemRead` omitted);
 *   - the in-app copy's own write to the OS clipboard FAILED
 *     (`systemWriteFailed`) — guard rail 7 (PSTUDIO-3612) keeps the in-memory
 *     copy authoritative in that case, so an internal Ctrl+C → Ctrl+V must keep
 *     working even though the OS clipboard is blank or stale.
 * An empty read with a healthy write is a genuinely empty clipboard → no-op.
 *
 * ## Why `reason` is split
 *
 *   - `denied`: nothing was readable AND the async read was rejected → the
 *     clipboard is blocked; tell the user once (symmetric to the write-side
 *     `notifyClipboardWriteFailed`, guard rail 7).
 *   - `empty`: the clipboard was reachable and simply holds nothing usable →
 *     silent no-op. Pasting nothing must not wipe the cell either.
 * NOTE (measured): a browser whose `clipboard-read` permission was never granted
 * RESOLVES the read with an empty string rather than rejecting it, so `denied` is
 * the narrower case in practice. That is fine — the `event` source covers the
 * real-browser path, and `empty` is a silent no-op rather than a wipe.
 *
 * Whitespace is content: a copied space pastes a space. Only a zero-length
 * payload counts as blank, so `trim()` is deliberately absent.
 *
 * Pure by design (no React, no design-system, no MobX imports) so it is unit
 * testable in this package's `environment: 'node'` Vitest setup — the norm for
 * branching logic here, per `.claude/rules/testing.md`.
 */

/** Outcome of an Async Clipboard API read attempt. `ok: false` = rejected. */
export type SystemClipboardRead =
  | { ok: true; value?: string | null }
  | { ok: false };

export type ResolveCellPasteTextInput = {
  /** `ClipboardEvent.clipboardData.getData('text/plain')`, if an event drove this paste. */
  eventText?: string | null;
  /** `ClipboardEvent.clipboardData.getData('text/html')` — carries our node envelope. */
  eventHtml?: string | null;
  /** Result of `navigator.clipboard.readText()`; omit when no read was attempted. */
  systemRead?: SystemClipboardRead;
  /** `clipboardDetails.valueAtTimeOfCopy` — the in-app single-cell copy buffer. */
  inMemory?: string;
  /**
   * True when the in-app copy could NOT be mirrored to the OS clipboard
   * (`clipboardDetails.systemWriteFailed`). Makes the in-memory buffer
   * authoritative even against an empty read — guard rail 7.
   */
  systemWriteFailed?: boolean;
  /**
   * True when NO native `paste` event drove this — the programmatic context-menu
   * Paste. A browser without `clipboard-read` RESOLVES the read empty rather than
   * rejecting, so on this path an empty read is indistinguishable from a blocked
   * one; with no event to speak for the clipboard, the in-app copy is the honest
   * source (guard rail 7 on the menu path — PSTUDIO-3968 review #5).
   */
  programmaticPaste?: boolean;
};

export type ResolvedCellPaste =
  | { kind: 'text'; value: string }
  | { kind: 'unavailable'; reason: 'denied' | 'empty' };

const hasContent = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.length > 0;

export function resolveCellPasteText(
  input: ResolveCellPasteTextInput
): ResolvedCellPaste {
  const {
    eventText,
    eventHtml,
    systemRead,
    inMemory,
    systemWriteFailed,
    programmaticPaste,
  } = input;

  if (hasContent(eventText)) {
    return { kind: 'text', value: eventText };
  }

  if (systemRead?.ok === true && hasContent(systemRead.value)) {
    return { kind: 'text', value: systemRead.value };
  }

  // See "Why the in-memory buffer needs a trust rule" above: an empty-but-
  // readable clipboard must NOT resurrect a stale in-app copy.
  const readWasRejected = systemRead?.ok === false;
  const noReadAttempted = systemRead === undefined;
  // On the programmatic (context-menu) path an empty read cannot be told apart
  // from a blocked one — no event corroborates it — so the in-app copy is the
  // honest source (guard rail 7, review #5). A native paste event that came back
  // empty is NOT this case: there the empty event is real evidence the clipboard
  // moved on, and the stale buffer must stay refused.
  const programmaticEmptyRead = programmaticPaste === true;
  const canTrustMemory =
    readWasRejected ||
    noReadAttempted ||
    systemWriteFailed === true ||
    programmaticEmptyRead;

  if (canTrustMemory && hasContent(inMemory)) {
    return { kind: 'text', value: inMemory };
  }

  // `denied` requires POSITIVE evidence that the clipboard was unreadable: the
  // async read was rejected AND the event handed us nothing at all. An event that
  // delivered html (a node bundle with no plain text) proves the clipboard was
  // reachable, so that is `empty`, not `denied` — no "blocked clipboard" toast for
  // a clipboard that plainly worked.
  const eventDeliveredSomething =
    hasContent(eventText) || hasContent(eventHtml);

  return {
    kind: 'unavailable',
    reason: readWasRejected && !eventDeliveredSomething ? 'denied' : 'empty',
  };
}
